"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { FilterGroupConfig } from "../directory-listing.config";
import { CATEGORIES_ATTRIBUTE, CATEGORY_PARAM, GROUP_KEY_TO_ATTRIBUTE } from "./algolia-listing.config";
import type { FacetIdNameMaps } from "./directory-facet-maps";
type TimeFilter = "upcoming" | "past";
interface RefinementItem {
  value: string;
  isRefined: boolean;
}
interface Refinement {
  items: RefinementItem[];
  refine: (value: string) => void;
}
function refinedValues(items: RefinementItem[]): Set<string> {
  return new Set(items.filter(i => i.isRefined).map(i => i.value));
}

// Strip zero-width / invisible characters (and trim). The facet maps are
// fetched server-side and serialized to the client, where invisible characters
// can creep into the payload; left in, they break the exact-match against the
// (clean) Algolia facet values, so every refinement silently returns nothing.
const INVISIBLE_RE = /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g;
function sanitizeName(name: string): string {
  return name.replace(INVISIBLE_RE, "").trim();
}

/** Toggle only the values whose membership differs from the target set. */
function reconcile({
  items,
  refine
}: Refinement, target: Set<string>) {
  const current = refinedValues(items);
  for (const value of new Set([...current, ...target])) {
    if (current.has(value) !== target.has(value)) refine(value);
  }
}
interface SyncedFacet {
  /** filter-group key, or the category sentinel */
  key: string;
  param: string;
  refinement: Refinement;
  /** facet value → URL value (Sanity id, or "N-star(s)") */
  toUrl: (facetValue: string) => string | null;
  /** URL value → facet value */
  toFacet: (urlValue: string) => string | null;
}
interface UseDirectoryUrlSyncArgs {
  filterGroupConfigs: FilterGroupConfig[];
  facetMaps: FacetIdNameMaps;
  byAttribute: Record<string, Refinement>;
  categories: Refinement;
  /**
   * Whether the category facet participates in URL sync. Articles drive
   * cross-category navigation through the curated menu links (which navigate to
   * archive routes), so they pass `false` to drop the `?category=<id>` contract
   * entirely — no reading deep links, no writing toggles.
   */
  categoryParamEnabled?: boolean;
  firstLetter: Refinement;
  query: string;
  setQuery: (q: string) => void;
  setTimeFilter: (t: TimeFilter) => void;
  setDateFrom: (d: string | null) => void;
  setDateTo: (d: string | null) => void;
}
const CATEGORY_KEY = "category";

/**
 * Keeps the directory's filter state in the URL using the exact contract the
 * GROQ path used (`?category=<sanityId>`, `?district=<id>`, `?letter=`, `?time=`,
 * `?from`/`?to`, `?q`) — so existing site deep links keep working and filtered
 * views stay shareable.
 *
 * Filtering stays client-side and instant: each handler applies the
 * InstantSearch refinement directly AND mirrors the new state into the URL via
 * `history.replaceState` — which, unlike the Next router, does not trigger a
 * server round-trip (re-rendering the route / re-fetching the facet maps), so
 * the Algolia search fires immediately instead of waiting on a navigation. The
 * URL is read back into state only on mount (deep links like `?category=<id>`)
 * and on back/forward. Since Algolia facets on resolved names but the URLs use
 * Sanity ids, values are translated via the `facetMaps` fetched server-side.
 */
export function useDirectoryUrlSync(t0) {
  const $ = _c(71);
  const {
    filterGroupConfigs,
    facetMaps,
    byAttribute,
    categories,
    categoryParamEnabled: t1,
    firstLetter,
    query,
    setQuery,
    setTimeFilter,
    setDateFrom,
    setDateTo
  } = t0;
  const categoryParamEnabled = t1 === undefined ? true : t1;
  let out;
  if ($[0] !== facetMaps) {
    out = {};
    for (const [attr, map] of Object.entries(facetMaps)) {
      const cleaned = {};
      for (const [id, name] of Object.entries(map)) {
        cleaned[id] = sanitizeName(name);
      }
      out[attr] = cleaned;
    }
    $[0] = facetMaps;
    $[1] = out;
  } else {
    out = $[1];
  }
  const cleanMaps = out;
  let inv;
  if ($[2] !== cleanMaps) {
    inv = {};
    for (const [attr_0, map_0] of Object.entries(cleanMaps)) {
      const reverse = {};
      for (const [id_0, name_0] of Object.entries(map_0)) {
        reverse[name_0] = id_0;
      }
      inv[attr_0] = reverse;
    }
    $[2] = cleanMaps;
    $[3] = inv;
  } else {
    inv = $[3];
  }
  const nameToId = inv;
  let list;
  if ($[4] !== byAttribute || $[5] !== categories || $[6] !== categoryParamEnabled || $[7] !== cleanMaps || $[8] !== filterGroupConfigs || $[9] !== nameToId) {
    list = [];
    if (categoryParamEnabled && cleanMaps[CATEGORIES_ATTRIBUTE]) {
      let t2;
      if ($[11] !== nameToId) {
        t2 = name_1 => nameToId[CATEGORIES_ATTRIBUTE]?.[sanitizeName(name_1)] ?? null;
        $[11] = nameToId;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      let t3;
      if ($[13] !== cleanMaps) {
        t3 = id_1 => cleanMaps[CATEGORIES_ATTRIBUTE]?.[id_1] ?? null;
        $[13] = cleanMaps;
        $[14] = t3;
      } else {
        t3 = $[14];
      }
      let t4;
      if ($[15] !== categories || $[16] !== t2 || $[17] !== t3) {
        t4 = {
          key: CATEGORY_KEY,
          param: CATEGORY_PARAM,
          refinement: categories,
          toUrl: t2,
          toFacet: t3
        };
        $[15] = categories;
        $[16] = t2;
        $[17] = t3;
        $[18] = t4;
      } else {
        t4 = $[18];
      }
      list.push(t4);
    }
    for (const group of filterGroupConfigs) {
      const attribute = GROUP_KEY_TO_ATTRIBUTE[group.key];
      if (!attribute) {
        continue;
      }
      const refinement = byAttribute[attribute];
      if (!refinement) {
        continue;
      }
      if (attribute === "starRating") {
        list.push({
          key: group.key,
          param: group.paramName,
          refinement,
          toUrl: _temp,
          toFacet: _temp2
        });
      } else {
        if (cleanMaps[attribute]) {
          list.push({
            key: group.key,
            param: group.paramName,
            refinement,
            toUrl: name_2 => nameToId[attribute]?.[sanitizeName(name_2)] ?? null,
            toFacet: id_3 => cleanMaps[attribute]?.[id_3] ?? null
          });
        }
      }
    }
    $[4] = byAttribute;
    $[5] = categories;
    $[6] = categoryParamEnabled;
    $[7] = cleanMaps;
    $[8] = filterGroupConfigs;
    $[9] = nameToId;
    $[10] = list;
  } else {
    list = $[10];
  }
  const syncedFacets = list;
  let t2;
  if ($[19] !== firstLetter || $[20] !== query || $[21] !== syncedFacets) {
    t2 = {
      syncedFacets,
      firstLetter,
      query
    };
    $[19] = firstLetter;
    $[20] = query;
    $[21] = syncedFacets;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  const latest = useRef(t2);
  let t3;
  if ($[23] !== firstLetter || $[24] !== query || $[25] !== syncedFacets) {
    t3 = () => {
      latest.current = {
        syncedFacets,
        firstLetter,
        query
      };
    };
    $[23] = firstLetter;
    $[24] = query;
    $[25] = syncedFacets;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  useEffect(t3);
  let t4;
  if ($[27] !== setDateFrom || $[28] !== setDateTo || $[29] !== setQuery || $[30] !== setTimeFilter) {
    t4 = () => {
      const {
        syncedFacets: facets,
        firstLetter: letterRefinement,
        query: currentQuery
      } = latest.current;
      const params = new URLSearchParams(window.location.search);
      for (const facet of facets) {
        const target = new Set(params.getAll(facet.param).map(facet.toFacet).filter(_temp3));
        reconcile(facet.refinement, target);
      }
      const letter = params.get("letter");
      reconcile(letterRefinement, letter ? new Set([letter]) : new Set());
      const q = params.get("q") ?? "";
      if (q !== currentQuery) {
        setQuery(q);
      }
      setTimeFilter(params.get("time") as TimeFilter ?? "upcoming");
      setDateFrom(params.get("from"));
      setDateTo(params.get("to"));
    };
    $[27] = setDateFrom;
    $[28] = setDateTo;
    $[29] = setQuery;
    $[30] = setTimeFilter;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  const applyUrlToState = t4;
  let t5;
  let t6;
  if ($[32] !== applyUrlToState) {
    t5 = () => {
      applyUrlToState();
      window.addEventListener("popstate", applyUrlToState);
      return () => window.removeEventListener("popstate", applyUrlToState);
    };
    t6 = [applyUrlToState];
    $[32] = applyUrlToState;
    $[33] = t5;
    $[34] = t6;
  } else {
    t5 = $[33];
    t6 = $[34];
  }
  useEffect(t5, t6);
  const updateUrl = _temp4;
  let map_1;
  if ($[35] !== syncedFacets) {
    map_1 = {};
    for (const facet_0 of syncedFacets) {
      map_1[facet_0.key] = facet_0;
    }
    $[35] = syncedFacets;
    $[36] = map_1;
  } else {
    map_1 = $[36];
  }
  const facetByKey = map_1;
  const toggleParam = _temp5;
  let t7;
  if ($[37] !== facetByKey) {
    t7 = name_3 => {
      const facet_1 = facetByKey[CATEGORY_KEY];
      const id_4 = facet_1?.toUrl(name_3);
      if (!facet_1 || !id_4) {
        return;
      }
      facet_1.refinement.refine(name_3);
      updateUrl(p => toggleParam(p, facet_1.param, id_4));
    };
    $[37] = facetByKey;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  const onCategoryToggle = t7;
  let t8;
  if ($[39] !== facetByKey) {
    t8 = (groupKey, value_0) => {
      const facet_2 = facetByKey[groupKey];
      const id_5 = facet_2?.toUrl(value_0);
      if (!facet_2 || !id_5) {
        return;
      }
      facet_2.refinement.refine(value_0);
      updateUrl(p_0 => toggleParam(p_0, facet_2.param, id_5));
    };
    $[39] = facetByKey;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  const onFilterToggle = t8;
  let t9;
  if ($[41] !== setDateFrom || $[42] !== setDateTo || $[43] !== syncedFacets) {
    t9 = (filters, dateRange) => {
      for (const facet_3 of syncedFacets) {
        reconcile(facet_3.refinement, filters[facet_3.key] ?? new Set());
      }
      if (dateRange) {
        setDateFrom(dateRange.from);
        setDateTo(dateRange.to);
      }
      updateUrl(params_2 => {
        for (const facet_4 of syncedFacets) {
          params_2.delete(facet_4.param);
        }
        for (const facet_5 of syncedFacets) {
          const values = filters[facet_5.key];
          if (!values) {
            continue;
          }
          for (const value_1 of values) {
            const id_6 = facet_5.toUrl(value_1);
            if (id_6) {
              params_2.append(facet_5.param, id_6);
            }
          }
        }
        if (dateRange) {
          if (dateRange.from) {
            params_2.set("from", dateRange.from);
          } else {
            params_2.delete("from");
          }
          if (dateRange.to) {
            params_2.set("to", dateRange.to);
          } else {
            params_2.delete("to");
          }
        }
      });
    };
    $[41] = setDateFrom;
    $[42] = setDateTo;
    $[43] = syncedFacets;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  const onApplyFilters = t9;
  let t10;
  if ($[45] !== firstLetter) {
    t10 = letter_0 => {
      reconcile(firstLetter, letter_0 ? new Set([letter_0]) : new Set());
      updateUrl(p_1 => {
        if (letter_0) {
          p_1.set("letter", letter_0);
        } else {
          p_1.delete("letter");
        }
      });
    };
    $[45] = firstLetter;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  const onLetterSelect = t10;
  let t11;
  if ($[47] !== firstLetter || $[48] !== setQuery) {
    t11 = q_0 => {
      if (q_0.trim()) {
        reconcile(firstLetter, new Set());
      }
      setQuery(q_0);
      updateUrl(p_2 => {
        if (q_0.trim()) {
          p_2.set("q", q_0);
          p_2.delete("letter");
        } else {
          p_2.delete("q");
        }
      });
    };
    $[47] = firstLetter;
    $[48] = setQuery;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  const onSearchChange = t11;
  let t12;
  if ($[50] !== setTimeFilter) {
    t12 = time => {
      setTimeFilter(time);
      updateUrl(p_3 => {
        if (time === "past") {
          p_3.set("time", "past");
        } else {
          p_3.delete("time");
        }
      });
    };
    $[50] = setTimeFilter;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  const onTimeFilterChange = t12;
  let t13;
  if ($[52] !== setDateFrom || $[53] !== setDateTo) {
    t13 = (from, to) => {
      setDateFrom(from);
      setDateTo(to);
      updateUrl(p_4 => {
        if (from) {
          p_4.set("from", from);
        } else {
          p_4.delete("from");
        }
        if (to) {
          p_4.set("to", to);
        } else {
          p_4.delete("to");
        }
      });
    };
    $[52] = setDateFrom;
    $[53] = setDateTo;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  const onDateRangeChange = t13;
  let t14;
  if ($[55] !== firstLetter || $[56] !== setDateFrom || $[57] !== setDateTo || $[58] !== setQuery || $[59] !== setTimeFilter || $[60] !== syncedFacets) {
    t14 = () => {
      for (const facet_6 of syncedFacets) {
        reconcile(facet_6.refinement, new Set());
      }
      reconcile(firstLetter, new Set());
      setQuery("");
      setTimeFilter("upcoming");
      setDateFrom(null);
      setDateTo(null);
      updateUrl(p_5 => {
        for (const facet_7 of syncedFacets) {
          p_5.delete(facet_7.param);
        }
        for (const key_0 of ["letter", "from", "to", "time", "q"]) {
          p_5.delete(key_0);
        }
      });
    };
    $[55] = firstLetter;
    $[56] = setDateFrom;
    $[57] = setDateTo;
    $[58] = setQuery;
    $[59] = setTimeFilter;
    $[60] = syncedFacets;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  const onClearFilters = t14;
  let t15;
  if ($[62] !== onApplyFilters || $[63] !== onCategoryToggle || $[64] !== onClearFilters || $[65] !== onDateRangeChange || $[66] !== onFilterToggle || $[67] !== onLetterSelect || $[68] !== onSearchChange || $[69] !== onTimeFilterChange) {
    t15 = {
      onCategoryToggle,
      onFilterToggle,
      onApplyFilters,
      onLetterSelect,
      onSearchChange,
      onTimeFilterChange,
      onDateRangeChange,
      onClearFilters
    };
    $[62] = onApplyFilters;
    $[63] = onCategoryToggle;
    $[64] = onClearFilters;
    $[65] = onDateRangeChange;
    $[66] = onFilterToggle;
    $[67] = onLetterSelect;
    $[68] = onSearchChange;
    $[69] = onTimeFilterChange;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  return t15;
}
function _temp5(params_1, key, value) {
  const current = params_1.getAll(key);
  params_1.delete(key);
  const next = current.includes(value) ? current.filter(v_0 => v_0 !== value) : [...current, value];
  next.forEach(v_1 => params_1.append(key, v_1));
}
function _temp4(mutate) {
  const params_0 = new URLSearchParams(window.location.search);
  mutate(params_0);
  const qs = params_0.toString();
  const url = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
  window.history.replaceState(window.history.state, "", url);
}
function _temp3(v) {
  return v != null;
}
function _temp2(id_2) {
  return id_2.match(/^(\d+)-star/)?.[1] ?? null;
}
function _temp(n) {
  return `${n}-star${n === "1" ? "" : "s"}`;
}